import React, { useCallback } from 'react';
import { Box, CSSProperties } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { figmaPxToWidthPercent, Report, useGetScalingSize } from '@ui/Report';
import { EnergyClassInfo } from '@shared/helpers/energy/energy-class';
import { ENERGY_CLASSES } from '@shared/helpers/energy/energy.models';
import { NodonTheme } from '@client/style';

/** Former `.8vw` at 1920px viewport — 15.36px on the report frame. */
const ENERGY_BAR_BORDER_RADIUS = figmaPxToWidthPercent(15.36);

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  energyBarSegments: {
    display: 'flex',
    borderRadius: getScalingSize(ENERGY_BAR_BORDER_RADIUS),
    gap: getScalingSize(figmaPxToWidthPercent(2)),
    overflow: 'hidden',
    aspectRatio: '482/46',
    width: '100%',
  },
  energyBarSegment: {
    flex: 1,
    height: '100%',
  },
  energyClassLabel: {
    marginLeft: getScalingSize(figmaPxToWidthPercent(9.6)),
    marginRight: getScalingSize(figmaPxToWidthPercent(9.6)),
  },
}));

type EnergyClassBarProps = {
  energyClassInfo: EnergyClassInfo;
  colorOverride?: string;
};
const DEFAULT_SEGMENTS = Object.freeze(
  Array.from({ length: ENERGY_CLASSES.length }, (_, index) => index),
);
export const EnergyClassBar: React.FC<EnergyClassBarProps> = ({
  energyClassInfo,
  colorOverride,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({ getScalingSize });
  const getSegmentStyle = useCallback(
    (index: number): CSSProperties => {
      const status =
        index < energyClassInfo.energyNumber ? 'active' : 'inactive';
      switch (status) {
        case 'active':
          return {
            backgroundColor: colorOverride ?? energyClassInfo.color,
          };
        case 'inactive':
          return {
            backgroundColor: NodonTheme.palette.grey[200],
          };
      }
    },
    [energyClassInfo, colorOverride],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: getScalingSize(figmaPxToWidthPercent(2)),
      }}
    >
      <Box className={classes.energyBarSegments}>
        {DEFAULT_SEGMENTS.map((id, index) => (
          <Box
            key={id}
            className={classes.energyBarSegment}
            sx={getSegmentStyle(index)}
          />
        ))}
      </Box>
      <Box className={classes.energyClassLabel}>
        <Report.Text fontWeight="bold" as="h4">
          {energyClassInfo.energyClass}
        </Report.Text>
      </Box>
    </Box>
  );
};
